<template>
  <div class="song-viewer-outer-wrapper flex flex-row w-full h-full min-h-0 relative items-stretch">
    <!-- Floating Drag Preview Avatar following cursor -->
    <div
      v-if="isDraggingSheet && dragPointerX > 0"
      class="fixed z-[99999] pointer-events-none px-3.5 py-1.5 rounded-xl bg-indigo-600 text-white shadow-2xl flex items-center gap-2 text-xs font-bold -translate-x-1/2 -translate-y-1/2 border border-white/20 select-none animate-pulse"
      :style="{ left: dragPointerX + 'px', top: dragPointerY + 'px' }"
    >
      <svg class="w-4 h-4 fill-current" viewBox="0 0 24 24">
        <circle cx="8" cy="6" r="1.6" /><circle cx="16" cy="6" r="1.6" />
        <circle cx="8" cy="12" r="1.6" /><circle cx="16" cy="12" r="1.6" />
        <circle cx="8" cy="18" r="1.6" /><circle cx="16" cy="18" r="1.6" />
      </svg>
      <span>{{ dragOverZone === 'left' ? t('songViewer.movingScoreLeft') : t('songViewer.movingScoreRight') }}</span>
    </div>

    <!-- Active Split Drop Zones when dragging a MusicSheet -->
    <div
      v-if="isDraggingSheet"
      class="absolute inset-0 z-50 pointer-events-none flex flex-row p-3 gap-3 bg-gray-900/20 backdrop-blur-[1px]"
    >
      <!-- Left Drop Target -->
      <div
        class="w-1/2 h-full border-3 border-dashed transition-all flex flex-col items-center justify-center rounded-2xl"
        :class="dragOverZone === 'left' ? 'border-indigo-600 bg-indigo-500/25 scale-[0.99] shadow-2xl' : 'border-indigo-400/40 bg-white/10 dark:bg-gray-800/30'"
      >
        <div class="bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 font-bold px-5 py-3 rounded-2xl shadow-xl flex items-center gap-2.5 text-sm sm:text-base border border-indigo-200 dark:border-indigo-800 pointer-events-none">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
          </svg>
          <span>{{ t('songViewer.splitDropLeft') }}</span>
        </div>
      </div>

      <!-- Right Drop Target -->
      <div
        class="w-1/2 h-full border-3 border-dashed transition-all flex flex-col items-center justify-center rounded-2xl"
        :class="dragOverZone === 'right' ? 'border-indigo-600 bg-indigo-500/25 scale-[0.99] shadow-2xl' : 'border-indigo-400/40 bg-white/10 dark:bg-gray-800/30'"
      >
        <div class="bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 font-bold px-5 py-3 rounded-2xl shadow-xl flex items-center gap-2.5 text-sm sm:text-base border border-indigo-200 dark:border-indigo-800 pointer-events-none">
          <span>{{ t('songViewer.splitDropRight') }}</span>
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 5l7 7-7 7M5 5l7 7-7 7" />
          </svg>
        </div>
      </div>
    </div>

    <!-- Main Container: Either Normal (Single Column) or Split (Two Columns) -->
    <div
      v-if="splitMode === 'none' || !hasMusicSheet"
      class="song-viewer-wrapper flex-1 min-w-0 md:p-4 bg-[#ffffff] dark:bg-gray-700 text-gray-900 dark:text-white"
      :class="[{ 'no-border': !border }, $attrs.class]"
    >
      <div ref="viewerRef" class="song-viewer" :style="{ zoom: props.zoom }" v-html="preprocessedContent || ''"></div>
    </div>

    <!-- Split Mode: 2 Columns Dashboard (Gridstack-style Resizable Columns) -->
    <div
      v-else
      ref="splitContainerRef"
      class="song-viewer-split-container flex-1 min-w-0 flex flex-row gap-0 h-full w-full p-2 md:p-3 overflow-hidden relative select-none"
    >
      <!-- Column 1 -->
      <div
        class="split-column split-column-1 relative group flex flex-col h-full min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden"
        :style="{ width: splitRatio + '%', flexShrink: 0 }"
      >
        <!-- Docked MusicSheet Column when splitMode === 'left' -->
        <template v-if="splitMode === 'left'">
          <!-- Floating Controls on Hover (No static header bar) -->
          <div class="absolute top-2 right-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-1 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
            <button
              type="button"
              @click="splitMode = 'right'"
              class="p-1 text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 rounded transition-colors cursor-pointer"
              :title="t('songViewer.moveToRight')"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
            </button>
            <button
              type="button"
              @click="splitMode = 'none'"
              class="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 rounded transition-colors cursor-pointer"
              :title="t('songViewer.closeSplit')"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <!-- MusicSheet Content: ONLY SVG AND PLAY BUTTON (CLEAN, NO OPTIONS/INSTRUMENTS/EXPORT) -->
          <div class="flex-1 min-h-0 overflow-auto p-4 flex items-start gap-3">
            <button
              type="button"
              @click.stop="toggleSplitPlay"
              class="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 cursor-pointer shrink-0 mt-2"
              :class="isSplitPlaying ? 'bg-amber-500 hover:bg-amber-600 text-white animate-pulse' : 'bg-emerald-600 hover:bg-emerald-700 text-white hover:scale-105'"
              :title="isSplitPlaying ? t('songViewer.pauseScore') : t('songViewer.listenScore')"
            >
              <svg v-if="isSplitPlaying" class="w-4 h-4 fill-current" viewBox="0 0 24 24">
                <rect x="6" y="6" width="12" height="12" rx="1.5" />
              </svg>
              <svg v-else class="w-4 h-4 fill-current ml-0.5" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
            <div class="flex-1 min-w-0">
              <MusicSheet
                ref="splitMusicSheetRef"
                :key="splitScoreKey"
                :locale="locale"
                :model-value="musicSheetAttrs.notesData || musicSheetAttrs.musicXmlData || musicSheetAttrs.abcData"
                :title="musicSheetAttrs.title"
                :time-signature="musicSheetAttrs.timeSignature"
                :key-signature="musicSheetAttrs.key"
                :tempo="musicSheetAttrs.tempo"
                :clef="musicSheetAttrs.clef"
                :wrap-mode="musicSheetAttrs.wrapMode || 'wrap'"
                :key-changes="musicSheetAttrs.keyChanges || []"
                :time-changes="musicSheetAttrs.timeChanges || []"
                :tempo-changes="musicSheetAttrs.tempoChanges || []"
                :tempo-ramps="musicSheetAttrs.tempoRamps || []"
                :readonly="true"
                :embedded="true"
                :fit-container="true"
                :zoom="props.zoom"
                @playback-state="(s) => isSplitPlaying = s"
                @playback-end="isSplitPlaying = false"
              />
            </div>
          </div>
        </template>

        <!-- Lyrics Column when splitMode === 'right' -->
        <template v-else>
          <div class="flex-1 min-h-0 overflow-auto p-4 song-viewer-wrapper no-border bg-[#ffffff] dark:bg-gray-700 text-gray-900 dark:text-white">
            <div ref="viewerRef" class="song-viewer" :style="{ zoom: props.zoom }" v-html="lyricsContentWithoutSheet || ''"></div>
          </div>
        </template>
      </div>

      <!-- Resizer Splitter Bar -->
      <div
        class="split-resizer-bar shrink-0 w-4 -mx-1 relative z-30 cursor-col-resize flex items-center justify-center group touch-none select-none"
        @pointerdown="startResizing"
        @touchstart.stop.prevent="startTouchResizing"
        @dblclick="splitRatio = 50"
        title="Arraste para redimensionar (duplo clique para 50%)"
      >
        <!-- Generous invisible touch target for finger tap on mobile/tablet (40px wide) -->
        <div class="absolute inset-y-0 -left-3 -right-3 cursor-col-resize touch-none"></div>
        <div class="w-1 h-full bg-gray-200 dark:bg-gray-700 group-hover:bg-indigo-500 group-active:bg-indigo-600 transition-colors rounded-full flex items-center justify-center">
          <div class="w-1.5 h-6 bg-gray-400 dark:bg-gray-500 group-hover:bg-indigo-500 group-active:bg-indigo-600 rounded-full shadow-xs"></div>
        </div>
      </div>

      <!-- Column 2 -->
      <div
        class="split-column split-column-2 relative group flex flex-col h-full min-h-0 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden"
        :style="{ width: `calc(${100 - splitRatio}% - 8px)`, flexShrink: 0 }"
      >
        <!-- Lyrics Column when splitMode === 'left' -->
        <template v-if="splitMode === 'left'">
          <div class="flex-1 min-h-0 overflow-auto p-4 song-viewer-wrapper no-border bg-[#ffffff] dark:bg-gray-700 text-gray-900 dark:text-white">
            <div ref="viewerRef" class="song-viewer" :style="{ zoom: props.zoom }" v-html="lyricsContentWithoutSheet || ''"></div>
          </div>
        </template>

        <!-- Docked MusicSheet Column when splitMode === 'right' -->
        <template v-else>
          <!-- Floating Controls on Hover (No static header bar) -->
          <div class="absolute top-2 right-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-1 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
            <button
              type="button"
              @click="splitMode = 'left'"
              class="p-1 text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 rounded transition-colors cursor-pointer"
              :title="t('songViewer.moveToLeft')"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
            </button>
            <button
              type="button"
              @click="splitMode = 'none'"
              class="p-1 text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 rounded transition-colors cursor-pointer"
              :title="t('songViewer.closeSplit')"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          <!-- MusicSheet Content: ONLY SVG AND PLAY BUTTON (CLEAN, NO OPTIONS/INSTRUMENTS/EXPORT) -->
          <div class="flex-1 min-h-0 overflow-auto p-4 flex items-start gap-3">
            <button
              type="button"
              @click.stop="toggleSplitPlay"
              class="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 cursor-pointer shrink-0 mt-2"
              :class="isSplitPlaying ? 'bg-amber-500 hover:bg-amber-600 text-white animate-pulse' : 'bg-emerald-600 hover:bg-emerald-700 text-white hover:scale-105'"
              :title="isSplitPlaying ? t('songViewer.pauseScore') : t('songViewer.listenScore')"
            >
              <svg v-if="isSplitPlaying" class="w-4 h-4 fill-current" viewBox="0 0 24 24">
                <rect x="6" y="6" width="12" height="12" rx="1.5" />
              </svg>
              <svg v-else class="w-4 h-4 fill-current ml-0.5" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </button>
            <div class="flex-1 min-w-0">
              <MusicSheet
                ref="splitMusicSheetRef"
                :key="splitScoreKey"
                :locale="locale"
                :model-value="musicSheetAttrs.notesData || musicSheetAttrs.musicXmlData || musicSheetAttrs.abcData"
                :title="musicSheetAttrs.title"
                :time-signature="musicSheetAttrs.timeSignature"
                :key-signature="musicSheetAttrs.key"
                :tempo="musicSheetAttrs.tempo"
                :clef="musicSheetAttrs.clef"
                :wrap-mode="musicSheetAttrs.wrapMode || 'wrap'"
                :key-changes="musicSheetAttrs.keyChanges || []"
                :time-changes="musicSheetAttrs.timeChanges || []"
                :tempo-changes="musicSheetAttrs.tempoChanges || []"
                :tempo-ramps="musicSheetAttrs.tempoRamps || []"
                :readonly="true"
                :embedded="true"
                :fit-container="true"
                :zoom="props.zoom"
                @playback-state="(s) => isSplitPlaying = s"
                @playback-end="isSplitPlaying = false"
              />
            </div>
          </div>
        </template>
      </div>
    </div>

    <SongCommentsSidebar
      v-model:visible="showCommentsSidebar"
      v-model:pinned="isCommentsSidebarPinned"
      :comments="commentsList"
      :editable="false"
      :activeCommentId="activeCommentId"
      @select-comment="highlightCommentInDocument"
    />
  </div>
</template>

<script setup>
import { defineProps, defineEmits, defineOptions, onMounted, onUnmounted, watch, nextTick, ref, computed, createVNode, render, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import { getSurroundingText, isWordChar, preprocessHtml } from '@/service/textEditor.service.js';
import { appEvents } from '@/store/app.events';
import SongCommentsSidebar from '@/components/editor/SongCommentsSidebar.vue';
import MusicSheetNodeView from '@/components/common/MusicSheetNodeView.vue';
import MusicSheet from '@/components/common/MusicSheet.vue';

const currentInstance = getCurrentInstance();
const { t, locale } = useI18n();

defineOptions({
  inheritAttrs: false
});

const props = defineProps({
  content: {
    type: String,
    default: ''
  },
  border: {
    type: Boolean,
    default: true
  },
  zoom: {
    type: [Number, String],
    default: 1
  }
});

const emit = defineEmits(['ready']);
const viewerRef = ref(null);

const mountedMusicSheetContainers = ref([]);
const isDraggingSheet = ref(false);
const dragOverZone = ref(null);
const dragPointerX = ref(0);
const dragPointerY = ref(0);
const splitMode = ref('none'); // 'none' | 'left' | 'right'
const splitRatio = ref(50); // percentage width for Column 1
const splitContainerRef = ref(null);
const splitMusicSheetRef = ref(null);
const isSplitPlaying = ref(false);

const MUSIC_SHEET_LAYOUT_KEY = 'mytriad_music_sheet_layout';

const loadSavedLayout = () => {
  try {
    const saved = localStorage.getItem(MUSIC_SHEET_LAYOUT_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.splitMode && ['none', 'left', 'right'].includes(parsed.splitMode)) {
        splitMode.value = parsed.splitMode;
      }
      if (typeof parsed.splitRatio === 'number' && parsed.splitRatio >= 20 && parsed.splitRatio <= 80) {
        splitRatio.value = parsed.splitRatio;
      }
    }
  } catch (e) {
    console.warn('Error reading music sheet layout from localStorage', e);
  }
};

const saveLayout = () => {
  try {
    localStorage.setItem(MUSIC_SHEET_LAYOUT_KEY, JSON.stringify({
      splitMode: splitMode.value,
      splitRatio: splitRatio.value
    }));
  } catch (e) {
    console.warn('Error saving music sheet layout to localStorage', e);
  }
};

const toggleSplitPlay = () => {
  if (splitMusicSheetRef.value) {
    splitMusicSheetRef.value.togglePlay();
  }
};

let isResizing = false;

const startResizing = (e) => {
  if (e.button !== 0 && e.pointerType === 'mouse') return;
  isResizing = true;
  window.__mytriad_is_resizing = true;
  window.__mytriad_last_interaction_time = Date.now();
  e.preventDefault();
  e.stopPropagation();

  if (e.target && e.target.setPointerCapture && e.pointerId !== undefined) {
    try {
      e.target.setPointerCapture(e.pointerId);
    } catch (_err) {
      // ignore pointer capture error on unsupported devices
    }
  }

  const onPointerMove = (moveEvent) => {
    if (!isResizing || !splitContainerRef.value) return;
    window.__mytriad_last_interaction_time = Date.now();
    const rect = splitContainerRef.value.getBoundingClientRect();
    const clientX = moveEvent.clientX;
    let newRatio = ((clientX - rect.left) / rect.width) * 100;
    if (newRatio < 20) newRatio = 20;
    if (newRatio > 80) newRatio = 80;
    splitRatio.value = Math.round(newRatio);
    triggerChordSpacing();
  };

  const onPointerUp = () => {
    isResizing = false;
    window.__mytriad_last_interaction_time = Date.now();
    setTimeout(() => {
      window.__mytriad_is_resizing = false;
    }, 400);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
    nextTick(() => {
      triggerChordSpacing();
    });
  };

  window.addEventListener('pointermove', onPointerMove);
  window.addEventListener('pointerup', onPointerUp);
};

const startTouchResizing = (e) => {
  isResizing = true;
  window.__mytriad_is_resizing = true;
  window.__mytriad_last_interaction_time = Date.now();
  e.stopPropagation();
  if (e.cancelable) e.preventDefault();

  const onTouchMove = (moveEvt) => {
    if (!isResizing || !splitContainerRef.value) return;
    window.__mytriad_last_interaction_time = Date.now();
    const touch = moveEvt.touches[0];
    if (!touch) return;
    const rect = splitContainerRef.value.getBoundingClientRect();
    let newRatio = ((touch.clientX - rect.left) / rect.width) * 100;
    if (newRatio < 20) newRatio = 20;
    if (newRatio > 80) newRatio = 80;
    splitRatio.value = Math.round(newRatio);
    triggerChordSpacing();
  };

  const onTouchEnd = () => {
    isResizing = false;
    window.__mytriad_last_interaction_time = Date.now();
    setTimeout(() => {
      window.__mytriad_is_resizing = false;
    }, 400);
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('touchend', onTouchEnd);
    window.removeEventListener('touchcancel', onTouchEnd);
    nextTick(() => {
      triggerChordSpacing();
    });
  };

  window.addEventListener('touchmove', onTouchMove, { passive: false });
  window.addEventListener('touchend', onTouchEnd);
  window.addEventListener('touchcancel', onTouchEnd);
};

const preprocessedContent = computed(() => preprocessHtml(props.content));

const hasMusicSheet = computed(() => {
  return /data-type="music-sheet"/i.test(preprocessedContent.value || '');
});

const musicSheetAttrs = computed(() => {
  if (!preprocessedContent.value) return {};
  try {
    const doc = new DOMParser().parseFromString(preprocessedContent.value, 'text/html');
    const el = doc.querySelector('div[data-type="music-sheet"]');
    if (!el) return {};

    const title = el.getAttribute('data-title') || 'Partitura';
    const timeSignature = el.getAttribute('data-time-signature') || '4/4';
    const key = el.getAttribute('data-key') || 'C';
    const tempo = parseInt(el.getAttribute('data-tempo'), 10) || 120;
    const clef = el.getAttribute('data-clef') || 'treble';
    const abcData = el.getAttribute('data-abc') || '';
    const musicXmlData = el.getAttribute('data-musicxml') || '';
    const wrapMode = el.getAttribute('data-wrap-mode') || 'wrap';

    let notesData = null;
    const rawNotes = el.getAttribute('data-notes');
    if (rawNotes) {
      try {
        notesData = JSON.parse(rawNotes);
      } catch (e) {
        notesData = null;
      }
    }

    let keyChanges = [];
    const rawKeyChanges = el.getAttribute('data-key-changes');
    if (rawKeyChanges) {
      try {
        keyChanges = JSON.parse(rawKeyChanges) || [];
      } catch (e) {
        keyChanges = [];
      }
    }

    let timeChanges = [];
    const rawTimeChanges = el.getAttribute('data-time-changes');
    if (rawTimeChanges) {
      try {
        timeChanges = JSON.parse(rawTimeChanges) || [];
      } catch (e) {
        timeChanges = [];
      }
    }

    let tempoChanges = [];
    const rawTempoChanges = el.getAttribute('data-tempo-changes');
    if (rawTempoChanges) {
      try {
        tempoChanges = JSON.parse(rawTempoChanges) || [];
      } catch (e) {
        tempoChanges = [];
      }
    }

    let tempoRamps = [];
    const rawTempoRamps = el.getAttribute('data-tempo-ramps');
    if (rawTempoRamps) {
      try {
        tempoRamps = JSON.parse(rawTempoRamps) || [];
      } catch (e) {
        tempoRamps = [];
      }
    }

    return {
      title,
      timeSignature,
      key,
      tempo,
      clef,
      abcData,
      musicXmlData,
      wrapMode,
      notesData,
      keyChanges,
      timeChanges,
      tempoChanges,
      tempoRamps
    };
  } catch (err) {
    console.warn('Error parsing musicSheetAttrs:', err);
    return {};
  }
});

const splitScoreKey = computed(() => {
  const attrs = musicSheetAttrs.value;
  return `${splitMode.value}-${attrs.title || ''}-${attrs.key || ''}-${attrs.tempo || ''}-${attrs.timeSignature || ''}-${(attrs.notesData ? attrs.notesData.length : 0)}`;
});

const lyricsContentWithoutSheet = computed(() => {
  if (!preprocessedContent.value) return '';
  try {
    const doc = new DOMParser().parseFromString(preprocessedContent.value, 'text/html');
    const sheets = doc.querySelectorAll('div[data-type="music-sheet"]');
    sheets.forEach(s => s.remove());
    return doc.body.innerHTML;
  } catch (e) {
    return preprocessedContent.value.replace(/<div\s+[^>]*data-type="music-sheet"[^>]*>[\s\S]*?<\/div>/gi, '');
  }
});


const handleDropOnZone = (zone) => {
  splitMode.value = zone;
  isDraggingSheet.value = false;
  dragOverZone.value = null;
  nextTick(() => {
    triggerChordSpacing();
    extractCommentsFromDom();
  });
};

const cleanupMountedMusicSheets = () => {
  if (viewerRef.value) {
    const allSheets = viewerRef.value.querySelectorAll('div[data-type="music-sheet"]');
    allSheets.forEach(el => {
      delete el.dataset.mountedMusicSheet;
    });
  }
  mountedMusicSheetContainers.value.forEach(container => {
    try {
      render(null, container);
      delete container.dataset.mountedMusicSheet;
    } catch (e) {
      console.warn('Error unmounting MusicSheetNodeView:', e);
    }
  });
  mountedMusicSheetContainers.value = [];
};

const renderEmbeddedMusicSheets = () => {
  if (!viewerRef.value || splitMode.value !== 'none') return;
  cleanupMountedMusicSheets();

  const musicSheetEls = viewerRef.value.querySelectorAll('div[data-type="music-sheet"]');
  musicSheetEls.forEach(el => {
    if (el.dataset.mountedMusicSheet) return;
    el.dataset.mountedMusicSheet = 'true';

    const title = el.getAttribute('data-title') || 'Partitura';
    const timeSignature = el.getAttribute('data-time-signature') || '4/4';
    const key = el.getAttribute('data-key') || 'C';
    const tempo = parseInt(el.getAttribute('data-tempo'), 10) || 120;
    const clef = el.getAttribute('data-clef') || 'treble';
    const abcData = el.getAttribute('data-abc') || '';
    const musicXmlData = el.getAttribute('data-musicxml') || '';
    const wrapMode = el.getAttribute('data-wrap-mode') || 'wrap';

    let notesData = null;
    const rawNotes = el.getAttribute('data-notes');
    if (rawNotes) {
      try {
        notesData = JSON.parse(rawNotes);
      } catch (e) {
        notesData = null;
      }
    }

    let keyChanges = [];
    const rawKeyChanges = el.getAttribute('data-key-changes');
    if (rawKeyChanges) {
      try {
        keyChanges = JSON.parse(rawKeyChanges) || [];
      } catch (e) {
        keyChanges = [];
      }
    }

    let timeChanges = [];
    const rawTimeChanges = el.getAttribute('data-time-changes');
    if (rawTimeChanges) {
      try {
        timeChanges = JSON.parse(rawTimeChanges) || [];
      } catch (e) {
        timeChanges = [];
      }
    }

    let tempoChanges = [];
    const rawTempoChanges = el.getAttribute('data-tempo-changes');
    if (rawTempoChanges) {
      try {
        tempoChanges = JSON.parse(rawTempoChanges) || [];
      } catch (e) {
        tempoChanges = [];
      }
    }

    let tempoRamps = [];
    const rawTempoRamps = el.getAttribute('data-tempo-ramps');
    if (rawTempoRamps) {
      try {
        tempoRamps = JSON.parse(rawTempoRamps) || [];
      } catch (e) {
        tempoRamps = [];
      }
    }

    const nodeMock = {
      attrs: {
        title,
        timeSignature,
        key,
        tempo,
        clef,
        abcData,
        musicXmlData,
        wrapMode,
        notesData,
        keyChanges,
        timeChanges,
        tempoChanges,
        tempoRamps,
        zoom: props.zoom
      }
    };

    const editorMock = {
      isEditable: false
    };

    const vnode = createVNode(MusicSheetNodeView, {
      node: nodeMock,
      editor: editorMock,
      zoom: props.zoom,
      updateAttributes: () => {}
    });

    if (currentInstance?.appContext) {
      vnode.appContext = currentInstance.appContext;
    }

    try {
      render(vnode, el);
      mountedMusicSheetContainers.value.push(el);
    } catch (err) {
      console.error('Error rendering MusicSheetNodeView in SongViewer:', err);
      delete el.dataset.mountedMusicSheet;
    }
  });
};



const adjustChordSpacing = () => {
  const container = viewerRef.value;
  if (!container) return;
  
  // Set class temporarily
  container.classList.add('measure-chords');
  void container.offsetHeight; // force layout
  
  const spacerMap = new Map();
  const spans = container.querySelectorAll('span.chord-span');
  
  const lineGroups = [];
  spans.forEach(span => {
    const rect = span.getBoundingClientRect();
    if (rect.width === 0) return;
    
    let group = lineGroups.find(g => Math.abs(g.top - rect.top) < 15);
    if (!group) {
      group = { top: rect.top, items: [] };
      lineGroups.push(group);
    }
    group.items.push({ span, rect });
  });
  
  lineGroups.forEach(group => {
    // Sort by DOM document position to guarantee correct flow order (left-to-right in DOM)
    const lineChords = group.items.sort((a, b) => {
      const position = a.span.compareDocumentPosition(b.span);
      if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
        return -1;
      }
      if (position & Node.DOCUMENT_POSITION_PRECEDING) {
        return 1;
      }
      return 0;
    });
    
    for (let i = 0; i < lineChords.length - 1; i++) {
      const current = lineChords[i];
      const next = lineChords[i + 1];
      
      const currentRight = current.rect.right;
      const nextLeft = next.rect.left;
      
      const requiredGap = 14; // minimum gap in px between chords
      if (currentRight + requiredGap > nextLeft) {
        const overlap = (currentRight + requiredGap) - nextLeft;
        const parentSup = current.span.closest('sup.chord-sup');
        if (parentSup) {
          const { beforeChar, afterChar } = getSurroundingText(parentSup);
          const isWithinWord = isWordChar(beforeChar) && isWordChar(afterChar);
          const spacerChar = isWithinWord ? '_' : ' ';
          
          // Add a temporary hidden spacer character to calculate the exact width needed
          const measureSpan = document.createElement('span');
          measureSpan.style.visibility = 'hidden';
          measureSpan.style.position = 'absolute';
          measureSpan.style.whiteSpace = 'pre';
          const computedStyle = window.getComputedStyle(parentSup);
          measureSpan.style.font = computedStyle.font;
          measureSpan.style.fontWeight = 'bold'; // Match the ::after styling
          measureSpan.textContent = spacerChar;
          document.body.appendChild(measureSpan);
          const charWidth = measureSpan.getBoundingClientRect().width || 8;
          document.body.removeChild(measureSpan);
          
          const numChars = Math.ceil(overlap / charWidth);
          if (numChars > 0) {
            const spacer = spacerChar.repeat(numChars);
            spacerMap.set(parentSup, spacer);
          }
        }
      }
    }
  });
  
  container.classList.remove('measure-chords');
  
  // Apply spacers directly to DOM
  const sups = container.querySelectorAll('sup.chord-sup');
  sups.forEach(sup => {
    const newSpacer = spacerMap.get(sup) || '';
    if (newSpacer) {
      sup.setAttribute('data-spacer', newSpacer);
    } else {
      sup.removeAttribute('data-spacer');
    }
  });
};

const triggerChordSpacing = () => {
  adjustChordSpacing();
  setTimeout(adjustChordSpacing, 50);
  setTimeout(adjustChordSpacing, 200);
  setTimeout(adjustChordSpacing, 500);
};

const commentsList = ref([]);
const showCommentsSidebar = ref(false);
const isCommentsSidebarPinned = ref(false);
const activeCommentId = ref(null);

const loadPinnedState = () => {
  try {
    const saved = localStorage.getItem('song-comments-sidebar-pinned');
    if (saved !== null) {
      isCommentsSidebarPinned.value = JSON.parse(saved);
    }
  } catch (e) {
    console.error('Error loading pinned state:', e);
  }
};

const extractCommentsFromDom = () => {
  if (!viewerRef.value) return;
  const list = [];
  const commentElements = viewerRef.value.querySelectorAll('.comment-mark, [data-comment-id]');
  commentElements.forEach((el, index) => {
    let id = el.getAttribute('data-comment-id');
    if (!id) {
      id = `comment-${index}-${Math.random().toString(36).substr(2, 6)}`;
      el.setAttribute('data-comment-id', id);
    }
    const commentText = el.getAttribute('data-comment-text') || el.getAttribute('title') || '';
    const selectedText = el.textContent || '';
    if (!list.some(c => c.id === id)) {
      list.push({
        id,
        commentText,
        selectedText
      });
    }
  });
  commentsList.value = list;
};

const highlightCommentInDocument = (id) => {
  if (!viewerRef.value) return;
  activeCommentId.value = id;
  const elements = viewerRef.value.querySelectorAll('.comment-mark, [data-comment-id]');
  elements.forEach(el => {
    if (el.getAttribute('data-comment-id') === id) {
      el.classList.add('comment-mark-active');
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => {
        el.classList.remove('comment-mark-active');
      }, 2500);
    } else {
      el.classList.remove('comment-mark-active');
    }
  });
};

const handleViewerClick = (event) => {
  const commentMark = event.target.closest('.comment-mark, [data-comment-id]');
  if (commentMark) {
    let id = commentMark.getAttribute('data-comment-id');
    if (!id) {
      id = Math.random().toString(36).substr(2, 9);
      commentMark.setAttribute('data-comment-id', id);
    }
    showCommentsSidebar.value = true;
    highlightCommentInDocument(id);
    return;
  }
  handleChordClick(event);
};

const handleToggleNotes = (val) => {
  showCommentsSidebar.value = val !== undefined ? val : !showCommentsSidebar.value;
};

let resizeObserver = null;

const handleChordClick = (event) => {
  const chordSpan = event.target.closest('.chord-span');
  if (chordSpan) {
    const chordText = chordSpan.textContent.trim();
    if (chordText) {
      appEvents.emit('chordClicked', chordText);
    }
  }
};

const onSheetPointerStart = (e) => {
  if (hasMusicSheet.value) {
    window.__mytriad_is_dragging = true;
    dragPointerX.value = e.detail?.clientX || 0;
    dragPointerY.value = e.detail?.clientY || 0;
  }
};

const onSheetPointerMove = (e) => {
  if (!hasMusicSheet.value) return;
  const clientX = e.detail?.clientX || 0;
  const clientY = e.detail?.clientY || 0;
  dragPointerX.value = clientX;
  dragPointerY.value = clientY;

  if (e.detail?.hasMoved) {
    window.__mytriad_is_dragging = true;
    isDraggingSheet.value = true;
    dragOverZone.value = clientX < window.innerWidth / 2 ? 'left' : 'right';
  }
};

const onSheetPointerEnd = (e) => {
  if (!hasMusicSheet.value) return;
  if (e.detail?.hasMoved) {
    const chosenZone = (e.detail?.clientX || dragPointerX.value) < window.innerWidth / 2 ? 'left' : 'right';
    handleDropOnZone(chosenZone);
  }
  isDraggingSheet.value = false;
  dragOverZone.value = null;
  dragPointerX.value = 0;
  dragPointerY.value = 0;
  setTimeout(() => {
    window.__mytriad_is_dragging = false;
  }, 200);
};

const onSheetDragStart = () => {
  if (hasMusicSheet.value) {
    window.__mytriad_is_dragging = true;
    isDraggingSheet.value = true;
  }
};

const onSheetDragEnd = () => {
  isDraggingSheet.value = false;
  dragOverZone.value = null;
  setTimeout(() => {
    window.__mytriad_is_dragging = false;
  }, 200);
};

const onSheetToggleSplit = () => {
  if (splitMode.value === 'none') {
    splitMode.value = 'left';
  } else {
    splitMode.value = 'none';
  }
};

onMounted(() => {
  loadPinnedState();
  loadSavedLayout();
  if (window.ResizeObserver && viewerRef.value) {
    resizeObserver = new ResizeObserver(() => {
      triggerChordSpacing();
    });
    resizeObserver.observe(viewerRef.value);
  } else {
    window.addEventListener('resize', triggerChordSpacing);
  }
  if (viewerRef.value) {
    viewerRef.value.addEventListener('click', handleViewerClick);
  }
  appEvents.on('toggleNotes', handleToggleNotes);
  window.addEventListener('music-sheet-drag-start', onSheetDragStart);
  window.addEventListener('music-sheet-drag-end', onSheetDragEnd);
  window.addEventListener('music-sheet-toggle-split', onSheetToggleSplit);
  window.addEventListener('music-sheet-pointer-start', onSheetPointerStart);
  window.addEventListener('music-sheet-pointer-move', onSheetPointerMove);
  window.addEventListener('music-sheet-pointer-end', onSheetPointerEnd);
  nextTick(() => {
    triggerChordSpacing();
    extractCommentsFromDom();
    if (splitMode.value === 'none' || !hasMusicSheet.value) {
      renderEmbeddedMusicSheets();
    }
  });
  emit('ready');
});

onUnmounted(() => {
  cleanupMountedMusicSheets();
  if (resizeObserver) {
    resizeObserver.disconnect();
  } else {
    window.removeEventListener('resize', triggerChordSpacing);
  }
  if (viewerRef.value) {
    viewerRef.value.removeEventListener('click', handleViewerClick);
  }
  appEvents.off('toggleNotes', handleToggleNotes);
  window.removeEventListener('music-sheet-drag-start', onSheetDragStart);
  window.removeEventListener('music-sheet-drag-end', onSheetDragEnd);
  window.removeEventListener('music-sheet-toggle-split', onSheetToggleSplit);
  window.removeEventListener('music-sheet-pointer-start', onSheetPointerStart);
  window.removeEventListener('music-sheet-pointer-move', onSheetPointerMove);
  window.removeEventListener('music-sheet-pointer-end', onSheetPointerEnd);
});

watch(splitMode, (newVal) => {
  saveLayout();
  nextTick(() => {
    triggerChordSpacing();
    extractCommentsFromDom();
    if (newVal === 'none') {
      renderEmbeddedMusicSheets();
    }
  });
});

watch(splitRatio, () => {
  saveLayout();
});

watch(() => props.content, () => {
  loadSavedLayout();
  nextTick(() => {
    triggerChordSpacing();
    extractCommentsFromDom();
    if (splitMode.value === 'none' || !hasMusicSheet.value) {
      renderEmbeddedMusicSheets();
    }
  });
  emit('ready');
});

watch(() => props.zoom, () => {
  nextTick(() => {
    triggerChordSpacing();
    renderEmbeddedMusicSheets();
  });
});
</script>

<style scoped>
.song-viewer-outer-wrapper {
  width: 100%;
  height: 100%;
}
.song-viewer-wrapper {
  border: 1px solid #e2e8f0;
  padding: 24px;
  min-height: 250px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  text-align: left;
  overflow: auto;
}

:deep(html.dark) .song-viewer-wrapper {
  border-color: #1e293b;
  background-color: #0f172a;
}

.song-viewer-wrapper.no-border {
  border: none;
  padding: 24px;
}

@media (max-width: 768px) {
  .song-viewer-wrapper,
  .song-viewer-wrapper.no-border {
    padding: 10px !important;
  }
}

.song-viewer {
  font-size: 16px;
  line-height: 1.625;
  outline: none;
}

/* Base Headings & Paragraphs */
:deep(.song-viewer p) {
  position: relative;
  line-height: 2.5em;
  margin-bottom: 0;
}

:deep(.song-viewer h1) {
  font-size: 2.25rem;
  font-weight: 700;
  line-height: 1.25;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}

:deep(.song-viewer h2) {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.35;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

:deep(.song-viewer h3) {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.4;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

:deep(.song-viewer ul), :deep(.song-viewer ol) {
  padding-left: 1.5rem;
  margin-bottom: 1rem;
}

:deep(.song-viewer ul) {
  list-style-type: disc;
}

:deep(.song-viewer ol) {
  list-style-type: decimal;
}

:deep(sup.chord-sup:not([data-spacer])),
:deep(sup.chord-sup[data-spacer=""]) {
  position: relative;
  border-bottom: 2px dotted red;
  border-style: dotted;
  /* display: contents; */
}

:deep(sup.chord-sup[data-spacer]:not([data-spacer=""])) {
  position: relative;
  border-bottom: none;
  /* display: contents; */
}

:deep(.song-viewer span) {
  line-height: 2.5em;
}

:deep(sup.chord-sup span.chord-span) {
  position: absolute;
  top: -.6em;
  font-weight: bold;
  color: red;
  font-size: 18px;
  line-height: 1;
  user-select: none;
  white-space: nowrap;
}

/* Inline chord representations for specific blockquote sections */
:deep(.song-viewer blockquote.custom-blockquote.q-intro sup.chord-sup),
:deep(.song-viewer blockquote.custom-blockquote.q-arrange sup.chord-sup),
:deep(.song-viewer blockquote.custom-blockquote.q-solo sup.chord-sup),
:deep(.song-viewer blockquote.custom-blockquote.q-finishing sup.chord-sup) {
  vertical-align: baseline;
  font-size: inherit;
  top: 0;
}

:deep(.song-viewer blockquote.custom-blockquote.q-intro sup.chord-sup span.chord-span),
:deep(.song-viewer blockquote.custom-blockquote.q-arrange sup.chord-sup span.chord-span),
:deep(.song-viewer blockquote.custom-blockquote.q-solo sup.chord-sup span.chord-span),
:deep(.song-viewer blockquote.custom-blockquote.q-finishing sup.chord-sup span.chord-span) {
  position: relative;
  top: unset;
  display: inline-block;
  margin: 0 4px;
  vertical-align: baseline;
}

/* Custom Blockquotes */
:deep(.song-viewer blockquote.custom-blockquote) {
  position: relative;
  padding-left: 36px;
  margin: 1.5rem 0;
  min-height: 40px;
  font-style: normal;
  color: inherit !important;
}

:deep(.song-viewer blockquote.custom-blockquote em),
:deep(.song-viewer blockquote.custom-blockquote i) {
  font-style: italic !important;
}

:deep(.song-viewer blockquote.custom-blockquote::before) {
  content: attr(data-type);
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 26px;
  border-right: 2px dashed var(--blockquote-color, #6366f1);
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: var(--blockquote-color, #6366f1);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  user-select: none;
  white-space: nowrap;
  letter-spacing: 0.05em;
  padding-right: 4px;
}

:deep(.song-viewer blockquote.custom-blockquote.q-chorus) { 
  --blockquote-color: #3b82f6; 
  font-weight: bold;
}
:deep(.song-viewer blockquote.custom-blockquote.q-verse) { --blockquote-color: #10b981; }
:deep(.song-viewer blockquote.custom-blockquote.q-intro) { --blockquote-color: #6366f1; }
:deep(.song-viewer blockquote.custom-blockquote.q-arrange) { --blockquote-color: #6366f1; }
:deep(.song-viewer blockquote.custom-blockquote.q-bridge) { 
  --blockquote-color: #f59e0b; 
  font-style: italic;
}
:deep(.song-viewer blockquote.custom-blockquote.q-prelude) { 
  --blockquote-color: #8b5cf6; 
  font-style: italic;
}
:deep(.song-viewer blockquote.custom-blockquote.q-solo) { --blockquote-color: #ec4899; }
:deep(.song-viewer blockquote.custom-blockquote.q-note) {
  --blockquote-color: #64748b;
  background-color: rgba(255, 255, 176, 0.5);
  border: solid 1px rgb(229, 229, 67);
  border-radius: 4px;
}
:deep(.song-viewer blockquote.custom-blockquote.q-finishing) { --blockquote-color: #ef4444; }

/* Fallback type labels when data-type attribute is missing */
:deep(.song-viewer blockquote.custom-blockquote.q-chorus:not([data-type])::before) { content: "chorus"; }
:deep(.song-viewer blockquote.custom-blockquote.q-verse:not([data-type])::before) { content: "verse"; }
:deep(.song-viewer blockquote.custom-blockquote.q-intro:not([data-type])::before) { content: "intro"; }
:deep(.song-viewer blockquote.custom-blockquote.q-arrange:not([data-type])::before) { content: "arrange"; }
:deep(.song-viewer blockquote.custom-blockquote.q-bridge:not([data-type])::before) { content: "bridge"; }
:deep(.song-viewer blockquote.custom-blockquote.q-prelude:not([data-type])::before) { content: "prelude"; }
:deep(.song-viewer blockquote.custom-blockquote.q-solo:not([data-type])::before) { content: "solo"; }
:deep(.song-viewer blockquote.custom-blockquote.q-note:not([data-type])::before) { content: "note"; }
:deep(.song-viewer blockquote.custom-blockquote.q-finishing:not([data-type])::before) { content: "finishing"; }

/* Spacer display rules */
:deep(sup.chord-sup::after) {
  content: attr(data-spacer);
  white-space: pre;
  user-select: none;
  pointer-events: none;
  font-weight: bold;
  color: inherit;
}

:deep(sup.chord-sup[data-spacer*=" "]::after) {
  border-bottom: 2px dashed currentColor;
}

:deep(sup.chord-sup[data-spacer*="_"]::after) {
  border-bottom: none;
}

:deep(.song-viewer.measure-chords sup.chord-sup::after) {
  content: "" !important;
  display: none !important;
}

/* Vocal highlights */
:deep(.song-viewer span.vocal1),
:deep(.song-viewer span.vocal2),
:deep(.song-viewer span.vocal3),
:deep(.song-viewer span.vocal4),
:deep(.song-viewer span.vocal5),
:deep(.song-viewer span.vocal6),
:deep(.song-viewer span.vocal7),
:deep(.song-viewer span.vocal8),
:deep(.song-viewer span.vocal9),
:deep(.song-viewer span.vocal10) {
  position: relative;
  border-radius: 4px;
  padding: 0 4px;
}

:deep(.song-viewer span[class*="vocal"])::after {
  font-size: 10px;
  font-weight: 700;
  margin-left: 6px;
  display: inline-flex;
  align-items: center;
  height: 18px;
  padding: 0 6px;
  border-radius: 4px;
  user-select: none;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  vertical-align: middle;
  line-height: 1;
}

:deep(html.dark) .song-viewer span[class*="vocal"]::after {
  background-color: #1e293b;
  border-color: #334155;
}



:deep(.song-viewer span.vocal1)::after { content: " \2190  Voz 1"; color: var(--vocal1-color-text); border-color: rgba(155, 158, 232, 0.4); }
:deep(.song-viewer span.vocal2)::after { content: " \2190  Voz 2"; color: var(--vocal2-color-text); border-color: rgba(231, 187, 187, 0.4); }
:deep(.song-viewer span.vocal3)::after { content: " \2190  Voz 3"; color: #0d9488; border-color: rgba(13, 148, 136, 0.4); }
:deep(.song-viewer span.vocal4)::after { content: " \2190  Voz 4"; color: var(--vocal4-color-text); border-color: rgba(239, 168, 106, 0.4); }
:deep(.song-viewer span.vocal5)::after { content: " \2190  Voz 5"; color: var(--vocal5-color-text); border-color: rgba(126, 212, 202, 0.4); }
:deep(.song-viewer span.vocal6)::after { content: " \2190  Voz 6"; color: var(--vocal6-color-text); border-color: rgba(160, 198, 120, 0.4); }
:deep(.song-viewer span.vocal7)::after { content: " \2190  Voz 7"; color: var(--vocal7-color-text); border-color: rgba(198, 212, 126, 0.4); }
:deep(.song-viewer span.vocal8)::after { content: " \2190  Voz 8"; color: var(--vocal8-color-text); border-color: rgba(126, 212, 202, 0.4); }
:deep(.song-viewer span.vocal9)::after { content: " \2190  Voz 9"; color: var(--vocal9-color-text); border-color: rgba(155, 126, 212, 0.4); }
:deep(.song-viewer span.vocal10)::after { content: " \2190  Voz 10"; color: var(--vocal10-color-text); border-color: rgba(212, 126, 196, 0.4); }

:deep(.song-viewer span.vocal1) { background-color: var(--vocal1-color); }
:deep(.song-viewer span.vocal2) { background-color: var(--vocal2-color); }
:deep(.song-viewer span.vocal3) { background-color: var(--vocal3-color); }
:deep(.song-viewer span.vocal4) { background-color: var(--vocal4-color); }
:deep(.song-viewer span.vocal5) { background-color: var(--vocal5-color); }
:deep(.song-viewer span.vocal6) { background-color: var(--vocal6-color); }
:deep(.song-viewer span.vocal7) { background-color: var(--vocal7-color); }
:deep(.song-viewer span.vocal8) { background-color: var(--vocal8-color); }
:deep(.song-viewer span.vocal9) { background-color: var(--vocal9-color); }
:deep(.song-viewer span.vocal10) { background-color: var(--vocal10-color); }

/* Comment marks */
:deep(.song-viewer span.comment-mark) {
  border-bottom: 2px dashed #f59e0b;
  background-color: rgba(245, 158, 11, 0.08);
}
</style>
